/**
 * The feed's screen list (build spec 4.3), as a pure reducer so the scrolling rules of spec 6.1
 * are testable without a DOM.
 *
 * The invariant that makes the forward cap work without a server round trip: **only screens up
 * to `furthest + 1` exist**. The snap container simply ends there, so there is nothing to
 * scroll to — no cap to enforce in an event handler, no way to skip a card. Landing on a new
 * furthest card appends the next screen and (in the component) sends a fire-and-forget
 * `PATCH /feed/position`; the server clamps anyway, so a lost request costs one card of
 * resume position and nothing else.
 *
 * The client never decides anything about learning state (ADR 0005): which topic comes next,
 * whether a topic is learned and whether the day goal was reached all arrive from the API.
 */

import type { AttemptResult, Chapter } from "../../shared/api-types.js";

export type Screen =
  | { kind: "card"; position: number }
  | { kind: "gate" }
  | { kind: "checkpoint" }
  | { kind: "result" }
  | { kind: "celebration" }
  | { kind: "allRead" }
  | { kind: "nextCard" }
  /** No next topic at all: the feed has nowhere to go, so it offers a retry instead. */
  | { kind: "noMore" };

export interface FeedState {
  /** The chapter whose cards, gate and checkpoint the list currently shows. */
  chapter: Chapter;
  screens: Screen[];
  /** The furthest card the server has agreed to; the list ends one screen past it. */
  furthestCard: number;
  /** Set once the fifth answer comes back; drives the result, celebration and allRead screens. */
  result: AttemptResult | null;
  /** The chapter the API prefetched with the result, shown as `nextCard` and swapped in later. */
  nextChapter: Chapter | null;
  /** Today's completed count for the day-goal ring; the result updates it. */
  today: number;
}

export type FeedAction =
  | { type: "loaded"; chapter: Chapter; today: number }
  | { type: "advance" }
  | { type: "gateConfirmed" }
  | { type: "result"; result: AttemptResult }
  | { type: "enterNextChapter" };

/** Spec 6.1: the gate sits one position past the last card, so it is just another screen. */
export function gatePosition(chapter: Chapter): number {
  return chapter.cards.length + 1;
}

/**
 * The screens for a chapter in its reading phase: every card up to `furthest`, plus one more so
 * there is somewhere to scroll to. At the last card that one more screen is the gate.
 */
function readingScreens(chapter: Chapter, furthestCard: number): Screen[] {
  const lastVisible = Math.min(furthestCard + 1, gatePosition(chapter));
  const screens: Screen[] = [];
  for (let position = 1; position <= Math.min(lastVisible, chapter.cards.length); position++) {
    screens.push({ kind: "card", position });
  }
  if (lastVisible >= gatePosition(chapter)) screens.push({ kind: "gate" });
  return screens;
}

/**
 * Build spec 4.3: a confirmed gate means the cards are unreachable for the rest of the chapter
 * (spec 6.1, the anti-cheat), so the list is the checkpoint alone.
 */
export function initialState(chapter: Chapter, today: number): FeedState {
  return {
    chapter,
    screens: chapter.gateConfirmed
      ? [{ kind: "checkpoint" }]
      : readingScreens(chapter, chapter.furthestCard),
    furthestCard: chapter.furthestCard,
    result: null,
    nextChapter: null,
    today,
  };
}

/** Where to scroll on mount: the resume position, or the checkpoint when the gate is behind us. */
export function initialScrollIndex(state: FeedState): number {
  if (state.chapter.gateConfirmed) return 0;
  const index = state.screens.findIndex(
    (screen) => screen.kind === "card" && screen.position === state.chapter.currentCard,
  );
  if (index >= 0) return index;
  // `currentCard` at the gate position resumes on the gate screen.
  const gateIndex = state.screens.findIndex((screen) => screen.kind === "gate");
  return gateIndex >= 0 ? gateIndex : 0;
}

export function feedReducer(state: FeedState, action: FeedAction): FeedState {
  switch (action.type) {
    case "loaded":
      return initialState(action.chapter, action.today);

    case "advance": {
      // Reading only: after the gate there is nothing to advance into.
      if (state.chapter.gateConfirmed || state.result) return state;
      const next = Math.min(state.furthestCard + 1, gatePosition(state.chapter));
      if (next === state.furthestCard) return state;
      return { ...state, furthestCard: next, screens: readingScreens(state.chapter, next) };
    }

    case "gateConfirmed":
      // Spec 6.1: from here the cards are gone for the rest of the chapter.
      return {
        ...state,
        chapter: { ...state.chapter, gateConfirmed: true },
        screens: [{ kind: "checkpoint" }],
      };

    case "result": {
      // Build spec 4.3: result, then the two conditional cards, then the next topic's cover.
      const screens: Screen[] = [{ kind: "checkpoint" }, { kind: "result" }];
      if (action.result.dayGoalReached) screens.push({ kind: "celebration" });
      if (action.result.allRead) screens.push({ kind: "allRead" });
      // Build spec 3.3: `next` is null only when the category has no published topic left at
      // all. Without a screen here the student is stranded on the result card with nothing to
      // scroll to and no way back, so the feed says so and offers a retry.
      screens.push(action.result.next ? { kind: "nextCard" } : { kind: "noMore" });
      return {
        ...state,
        screens,
        result: action.result,
        nextChapter: action.result.next,
        today: action.result.today,
      };
    }

    case "enterNextChapter": {
      // Scrolling past the next topic's cover swaps chapters and drops every earlier screen,
      // so a finished topic cannot be scrolled back into (build spec 8).
      if (!state.nextChapter) return state;
      return initialState(state.nextChapter, state.today);
    }

    default:
      return state;
  }
}

/** The index the feed scrolls to when the fifth answer lands: the result screen. */
export function resultScrollIndex(state: FeedState): number {
  const index = state.screens.findIndex((screen) => screen.kind === "result");
  return index >= 0 ? index : 0;
}
